/**
 * The browser's live socket: the assistant's call
 * (`/v1/agent/assistant/live`), on the assistant's seat or a setup seat.
 *
 * The phone's `assistant-live-socket.ts` opens React Native's WebSocket with
 * a third argument of headers — the cookie and the tenant — and passes `null`
 * for the protocols. A browser has no third argument, and reads that `null`
 * as a protocol named "null", which the server never answers and the browser
 * then fails. So the socket is opened here, and everything else is the
 * phone's: the path (`assistantLiveWsUrl`), the reader
 * (`readAssistantLiveServerMessage`), the handler and socket shapes, and the
 * one race they guard — `start` goes first, whichever of the socket opening or
 * the first microphone frame gets there first, because the server closes 1008
 * on any binary before `start`.
 *
 * The socket adds one way to send, `frame`, for the version-2 frames
 * (`attention`, the person's named moves, the setup app's `app_note`). A
 * browser WebSocket has no headers, so everything a capable call declares
 * (its capabilities, a resume or a takeover) rides the first
 * JSON frame, `start`, and nothing personal goes in the query.
 *
 * The cookie travels by itself (same origin). The seat, the tenant and the
 * client ride in the query (`live-urls.ts`): the setup seat (`?seat=setup`)
 * sends no tenant, since it composes a business that does not exist yet (or
 * edits one through its session); the assistant sends one. Every socket names the app as
 * `?client=`, as the header every fetch sends.
 */
import type { AssistantLiveClientMessage, AssistantLiveContext, AssistantLiveServerMessage, LiveClientFrame, LiveSeat } from '@alluzo/shared';

import { apiBaseUrl } from '@/lib/api';
import { HAND_OFF_MS, type AssistantLiveHandlers, type AssistantLiveSocket } from '@/lib/assistant-live-socket';
import { assistantLiveWsUrl, readAssistantLiveServerMessage } from '@/lib/assistant-live-wire';
import { WEB_CLIENT } from '~/platform/fetch';

import { withClient, withSeat, withTenant } from './live-urls';

export type { AssistantLiveHandlers, AssistantLiveSocket };

/**
 * The assistant's URL: inside one business on the assistant's seat, and with
 * no business on the setup seat.
 */
export const assistantLiveUrl = (tenantId: string | null, seat?: LiveSeat): string =>
  withClient(withTenant(withSeat(assistantLiveWsUrl(apiBaseUrl), seat), seat === 'setup' ? null : tenantId), WEB_CLIENT);

type Handlers<Server> = {
  readonly onAudio: (pcm: Uint8Array) => void;
  readonly onMessage: (message: Server) => void;
  readonly onClose: (code: number, reason: string) => void;
  readonly onError: () => void;
};

type Opened<Client> = {
  readonly ws: WebSocket;
  /** Whether the frame went out: one sent while the socket is not open is dropped. */
  readonly send: (message: Client) => boolean;
  readonly sendAudio: (pcm: Uint8Array) => void;
  readonly close: () => void;
};

/** One socket: JSON control frames both ways, binary PCM16 for the audio. */
const openSocket = <Client extends { readonly type: string }, Server>(
  url: string,
  start: Client,
  read: (raw: unknown) => Server | null,
  handlers: Handlers<Server>,
  /** Once `start` has gone: what was asked for before the socket was up goes now. */
  onStarted?: () => void,
): Opened<Client> => {
  const ws = new WebSocket(url);
  // The voice arrives as bytes, not as a Blob read asynchronously: by the
  // time a Blob resolved, the sound would be late.
  ws.binaryType = 'arraybuffer';

  const send = (message: Client): boolean => {
    if (ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(message));
    return true;
  };

  // The microphone is already running when this socket is made, and a frame
  // can reach the page in the same task the socket opens, before `onopen`.
  let started = false;
  const sendStart = (): void => {
    if (started || ws.readyState !== WebSocket.OPEN) return;
    send(start);
    started = true;
    onStarted?.();
  };

  ws.onopen = () => sendStart();
  ws.onmessage = (event: MessageEvent<unknown>) => {
    const data = event.data;
    if (data instanceof ArrayBuffer) {
      handlers.onAudio(new Uint8Array(data));
      return;
    }
    const message = read(data);
    if (message !== null) handlers.onMessage(message);
  };
  ws.onclose = (event) => handlers.onClose(event.code, event.reason);
  ws.onerror = () => handlers.onError();

  return {
    ws,
    send,
    sendAudio: (pcm) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      sendStart();
      if (!started) return;
      ws.send(pcm);
    },
    close: () => {
      // The handlers belong to a call that is over; a close event now would
      // walk the screen back into a state it has left.
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.close();
    },
  };
};

/**
 * The browser's assistant socket: the phone's, and one more way to send.
 * `frame` sends one version-2 frame (`attention`, `attend`, `resume_work`,
 * `quiet`, `carry_on`, `app_note`, …), answering whether it went out; a
 * legacy call never sends one.
 */
export type WebAssistantLiveSocket = AssistantLiveSocket & {
  readonly frame: (frame: LiveClientFrame) => boolean;
  /**
   * Files read, by id (Decided 19 of docs/plans/setup-from-files.md; on the
   * setup seat, Decided 9): OS is told what they say. Ids sent before the
   * socket is up go right after `start`.
   */
  readonly files: (fileIds: readonly string[]) => void;
};

export const connectAssistantLive = (
  tenantId: string | null,
  context: AssistantLiveContext,
  handlers: AssistantLiveHandlers,
  seat?: LiveSeat,
): WebAssistantLiveSocket => {
  /** Files asked for before `start` went: they go right after it. */
  let filesEarly: readonly string[] = [];
  const opened = openSocket<AssistantLiveClientMessage, AssistantLiveServerMessage>(
    assistantLiveUrl(tenantId, seat),
    { type: 'start', context },
    readAssistantLiveServerMessage,
    handlers,
    () => {
      if (filesEarly.length > 0) opened.send({ type: 'files', fileIds: filesEarly });
      filesEarly = [];
    },
  );
  const { ws } = opened;

  let backstop: ReturnType<typeof setTimeout> | null = null;
  let done: (() => void) | null = null;
  const over = (): void => {
    if (backstop !== null) clearTimeout(backstop);
    backstop = null;
    const held = done;
    done = null;
    held?.();
  };

  const close = (): void => {
    opened.close();
    over();
  };

  const { send } = opened;
  return {
    frame: (frame) => send(frame),
    sendAudio: opened.sendAudio,
    mute: (muted) => {
      send({ type: 'mute', muted });
    },
    stop: () => {
      send({ type: 'stop' });
    },
    close,
    cancel: (messageId) => {
      send(messageId === undefined ? { type: 'cancel' } : { type: 'cancel', messageId });
    },
    interrupt: () => {
      send({ type: 'interrupt' });
    },
    say: (text) => send({ type: 'say', text }),
    files: (fileIds) => {
      if (fileIds.length === 0) return;
      if (!send({ type: 'files', fileIds })) filesEarly = [...new Set([...filesEarly, ...fileIds])];
    },
    view: (look) => {
      send({ type: 'view', look });
    },
    place: ({ screen, recordId, cards, view }) => {
      send({
        type: 'place',
        screen,
        ...(recordId === undefined ? {} : { recordId }),
        ...(cards === undefined ? {} : { cards }),
        ...(view === undefined ? {} : { view }),
      });
    },
    cardResult: (result) => {
      send({ type: 'card_result', ...result });
    },
    handOff: (onTurn, onDone, options) => {
      done = onDone;
      // A socket that never opened carried no question: nothing is owed.
      if (ws.readyState !== WebSocket.OPEN) {
        close();
        return;
      }
      if (options?.sayStop !== false) send({ type: 'stop' });
      ws.onerror = null;
      ws.onmessage = (event: MessageEvent<unknown>) => {
        if (event.data instanceof ArrayBuffer) return;
        const message = readAssistantLiveServerMessage(event.data);
        if (message?.type === 'turn') onTurn(message);
        else if (message?.type === 'asking' || message?.type === 'progress') options?.onWork?.(message);
      };
      ws.onclose = () => over();
      backstop = setTimeout(close, HAND_OFF_MS);
    },
  };
};
